import { z } from 'zod';
import {
  MEMBER_PROFILE_IDENTITY_TEXT_MAX,
  registrationProfileAddressSchema,
  sexAssignedAtBirthSchema,
} from '@galapagos/shared';
import { otpChannelSchema } from '@galapagos/shared/api/auth';
import { SIGNUP_FLOW_STORAGE_KEY, removeStoredSignupFlow } from '@/services/signup-flow-storage';
import { parseLegacySignupParams } from '../services/legacy-params';

/**
 * The one key the signup flow keeps its answers under, for this tab only, so a
 * reload or a back/forward into a signup page can pick up where the member
 * was. Nothing else in signup writes member answers to browser storage.
 */
export { SIGNUP_FLOW_STORAGE_KEY, removeStoredSignupFlow };

const STORAGE_VERSION = 1;

const storedParamsSchema = z
  .object({
    clientId: z.string().optional(),
    subClientId: z.string().optional(),
    language: z.enum(['en', 'es']).optional(),
    directToReg: z.boolean(),
    login: z.string().optional(),
    download: z.string().optional(),
    flow: z.string().optional(),
  })
  .strict();

const storedLegalNameSchema = z
  .object({ firstName: z.string().min(1), lastName: z.string().min(1) })
  .strict();

const storedSignInContactSchema = z
  .object({ channel: otpChannelSchema, destination: z.string().min(1) })
  .strict();

const storedIdentityTextSchema = z
  .string()
  .min(1)
  .max(MEMBER_PROFILE_IDENTITY_TEXT_MAX)
  .nullable()
  .default(null);

/**
 * Exactly the answers the flow needs to resume, and nothing it can read back
 * from the server: no tokens, codes, eligibility results or health-plan
 * numbers. Strict, so a value written by some other code (or an
 * older shape) is dropped rather than half-trusted.
 */
const storedSignupFlowSchema = z
  .object({
    v: z.literal(STORAGE_VERSION),
    params: storedParamsSchema.nullable(),
    programSelection: z.array(z.string()).nullable(),
    chatChosen: z.boolean(),
    legalName: storedLegalNameSchema.nullable(),
    signInContact: storedSignInContactSchema.nullable(),
    profileAddress: registrationProfileAddressSchema.nullable(),
    sexAssignedAtBirth: sexAssignedAtBirthSchema.nullable(),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable(),
    sponsorRef: z.string().nullable(),
    // Added after the first stored shape: a record kept by an older page in
    // this tab has neither, and still restores.
    genderIdentity: storedIdentityTextSchema,
    pronouns: storedIdentityTextSchema,
    // Added later still: whether the welcome-back page started this flow for
    // a member already signed in, so the account and code steps step aside.
    resumedSignIn: z.boolean().default(false),
    // A pending completion keeps its original source across a same-tab reload.
    onboardingSourceId: z.string().uuid().optional(),
  })
  .strict();

/** The flow store fields kept for this tab. */
export type StoredSignupFlowAnswers = Omit<z.infer<typeof storedSignupFlowSchema>, 'v'>;

/** The minimum of a `PerformanceNavigationTiming` the restore decision reads. */
export interface SignupNavigationEntry {
  type: string;
  /** The URL the document was loaded at. */
  name: string;
}

export type SignupFlowRestoreDecision = 'restore' | 'wipe';

/**
 * Whether this document load may pick the stored answers back up: only a
 * reload or a back/forward, and only when the document was loaded on a signup
 * page. A fresh visit (a typed URL, a new link), a prerender, or a document
 * first loaded elsewhere starts clean.
 */
export function signupFlowRestoreDecision(
  entry: SignupNavigationEntry | undefined,
  isSignupPagePath: (pathname: string) => boolean,
): SignupFlowRestoreDecision {
  if (entry === undefined) return 'wipe';
  if (entry.type !== 'reload' && entry.type !== 'back_forward') return 'wipe';
  let pathname: string;
  try {
    pathname = new URL(entry.name).pathname;
  } catch {
    return 'wipe';
  }
  return isSignupPagePath(pathname) ? 'restore' : 'wipe';
}

/** This document's navigation entry, or undefined when the browser cannot say. */
export function readSignupNavigationEntry(): SignupNavigationEntry | undefined {
  try {
    const [entry] = window.performance.getEntriesByType('navigation');
    if (entry === undefined || !('type' in entry) || typeof entry.type !== 'string') {
      return undefined;
    }
    return { type: entry.type, name: entry.name };
  } catch {
    return undefined;
  }
}

// Every storage touch is guarded: the accessor itself can throw in privacy
// modes, and a full or blocked store must leave the flow working in memory.
// Failures are deliberately silent, so no answer can reach a log.

/** The stored answers, or null when there are none or they fail the schema (which also clears them). */
export function readStoredSignupFlow(): StoredSignupFlowAnswers | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SIGNUP_FLOW_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeStoredSignupFlow();
    return null;
  }
  const result = storedSignupFlowSchema.safeParse(parsed);
  if (!result.success) {
    removeStoredSignupFlow();
    return null;
  }
  const stored = result.data;
  return {
    ...(stored.onboardingSourceId ? { onboardingSourceId: stored.onboardingSourceId } : {}),
    params: stored.params,
    programSelection: stored.programSelection,
    chatChosen: stored.chatChosen,
    legalName: stored.legalName,
    signInContact: stored.signInContact,
    profileAddress: stored.profileAddress,
    sexAssignedAtBirth: stored.sexAssignedAtBirth,
    birthDate: stored.birthDate,
    sponsorRef: stored.sponsorRef,
    genderIdentity: stored.genderIdentity,
    pronouns: stored.pronouns,
    resumedSignIn: stored.resumedSignIn,
  };
}

function holdsNothing(answers: StoredSignupFlowAnswers): boolean {
  return (
    answers.onboardingSourceId === undefined &&
    answers.params === null &&
    answers.programSelection === null &&
    !answers.chatChosen &&
    answers.legalName === null &&
    answers.signInContact === null &&
    answers.profileAddress === null &&
    answers.sexAssignedAtBirth === null &&
    answers.birthDate === null &&
    answers.sponsorRef === null &&
    answers.genderIdentity === null &&
    answers.pronouns === null &&
    !answers.resumedSignIn
  );
}

/** Keeps the answers for this tab; with nothing to keep, clears the key instead. */
export function writeStoredSignupFlow(answers: StoredSignupFlowAnswers): void {
  if (holdsNothing(answers)) {
    removeStoredSignupFlow();
    return;
  }
  try {
    window.sessionStorage.setItem(
      SIGNUP_FLOW_STORAGE_KEY,
      JSON.stringify({ v: STORAGE_VERSION, ...answers }),
    );
  } catch {
    // Storage full or blocked: the answers live in memory for this page only,
    // and an older copy must not outlive them to come back on a reload.
    removeStoredSignupFlow();
  }
}

/**
 * Whether the kept answers were given on the same sponsor link as the page
 * being loaded. A back/forward can land on an older page from another link,
 * and its answers must not carry into that one.
 */
function sameSponsorLink(answers: StoredSignupFlowAnswers, documentUrl: string): boolean {
  if (answers.params === null) return false;
  let search: string;
  try {
    search = new URL(documentUrl).search;
  } catch {
    return false;
  }
  const link = parseLegacySignupParams(search);
  return (
    answers.params.clientId === link.clientId && answers.params.subClientId === link.subClientId
  );
}

/**
 * The answers this document load starts with: the stored ones on a reload or
 * back/forward into signup on the same sponsor link, otherwise none (and the
 * key is cleared). Runs once, when the flow store is created; the static-export
 * prerender has no window.
 */
export function initialSignupFlowAnswers(
  isSignupPagePath: (pathname: string) => boolean,
): StoredSignupFlowAnswers | null {
  if (typeof window === 'undefined') return null;
  const entry = readSignupNavigationEntry();
  if (entry === undefined || signupFlowRestoreDecision(entry, isSignupPagePath) === 'wipe') {
    removeStoredSignupFlow();
    return null;
  }
  const answers = readStoredSignupFlow();
  if (answers === null) return null;
  if (!sameSponsorLink(answers, entry.name)) {
    removeStoredSignupFlow();
    return null;
  }
  return answers;
}
